import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    get as _get
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import BCTrail from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import SensorsService from '../data/sensors-service';
import IncidentService from '../data/incident-service';
import CMCsService from '../data/cmcs-service';

import RouteData, { EmptyRoutes } from '../data/routes';
import Auth, { User } from '../data/auth-service';
import { Typography } from '@mui/material';
import { TranslateHome } from '../libraries/helpers';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        height: '50px',
        width: '100%',
        marginTop: -4,
        paddingLeft: theme.spacing(4),
        transition: 'padding-left 500ms',
    },
    bcContainer: {
        height: 48,
        paddingTop: 7,
        transition: 'padding-left 500ms',
    },
    links: {
        ...theme.typography.h6,
        color: theme.palette.primary.main,
        // fontSize: '1.25rem',
        position: 'relative',
        zIndex: 10,
        textDecoration: 'none',
    },
    notALink: {
        color: theme.palette.primary.main,
        position: 'relative',
        fontSize: '22px'
    }
}));

const Breadcrumbs = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const [bc, setBC] = useState([]);
    const navigate = useNavigate();
    const user = User();
    const [sensorNames, setSensorNames] = useState({});
    const [incidentNames, setIncidentNames] = useState({});
    const [cmcNames, setCMCNames] = useState({});
    const sensorPerms = Auth.permissions.get('Sensors');
    const incidentPerms = Auth.permissions.get('Incidents');
    const cmcPerms = Auth.permissions.get('CMC');

    const getSensorNames = useCallback(() => {
        if ((process.env.BRICATA_APPLIANCE_TYPE === 'cmc' && sensorPerms?.read) || (sensorPerms?.read && cmcPerms?.read)) {
            SensorsService.getNames().then(res => {
                setSensorNames(res);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getIncidentNames = useCallback(() => {
        if (process.env.BRICATA_APPLIANCE_TYPE === 'cmc') {
            incidentPerms.read && IncidentService.getNames().then(res => {
                setIncidentNames(res);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCMCNames = useCallback(() => {
        if (process.env.BRICATA_APPLIANCE_TYPE === 'mc2') {
            cmcPerms.read && CMCsService.getNames().then(res => {
                setCMCNames(res);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getSensorNames();
        getIncidentNames();
        getCMCNames();

        let i;
        const crumbs = [];
        const l = props.path.split('/');

        l.splice(0, 1);

        for (i = 0; i < l.length; i++) {
            const rd = RouteData[RouteData.map((r) => { return r.path }).indexOf(l[i])] || { title: l[i] };
            rd.location = props.path.split('/', i + 2).join('/');
            crumbs.push(rd);
        }
        setBC(crumbs);
    }, [getCMCNames, getIncidentNames, getSensorNames, props.path]);

    const getFriendlyName = (name) => {
        if (props.path.match('incident')) {
            if (Object.prototype.hasOwnProperty.call(incidentNames, name)) {
                return incidentNames[name];
            }
        } else if (props.path.match('sensor')) {
            if (Object.prototype.hasOwnProperty.call(sensorNames, name)) {
                return sensorNames[name].name;
            }
        } else if (props.path.match('cmcs')) {
            if (Object.prototype.hasOwnProperty.call(cmcNames, name)) {
                return cmcNames[name];
            }
        }
        return name;
    };

    const handleNavigateUserHome = () => {
        const home = TranslateHome(_get(user, 'data.profile.home', '/'));
        navigate(home);
    };

    const handleLinkClick = (e, v) => {
        e.preventDefault();
        e.stopPropagation();

        navigate(v.location);
    };

    return (
        <div className={classes.root} style={{
            paddingLeft: props.offset + 40,
            marginTop: props.header ? '28px' : '0px',
        }}>
            <Grid container justifyContent="center">
                <Grid item xs={12}>
                    <Box className={classes.bcContainer} display="flex">
                        <Box mr={1}>
                            <Link
                                aria-label="app-link"
                                onClick={handleNavigateUserHome}
                                className={classes.links}
                                style={{ cursor: 'pointer' }}
                            >
                                <Typography variant="h6" color='primary'>
                                    {process.env.BRICATA_APPLIANCE_TYPE === 'mc2' ? 'MC2' : 'CMC'}
                                </Typography>
                            </Link>
                        </Box>
                        <Box mr={1} mt={0.5} color={theme.palette.text.secondary}>
                            <NavigateNextIcon />
                        </Box>
                        <BCTrail separator={<NavigateNextIcon />}>
                            {bc.map((v, i) => {
                                // /sensors/health
                                if (EmptyRoutes.reduce((a, c) => {
                                    if (a) return a;
                                    return c.test(v.location);
                                }, false)) {
                                    return <Box
                                        key={['bc', v, i].join('-')}
                                        className={classes.notALink}
                                    >
                                        {decodeURIComponent(getFriendlyName(v.title))}
                                    </Box>;
                                } else {
                                    return <Link
                                        className={classes.links}
                                        key={['bc', v, i].join('-')}
                                        aria-label={v.title.toLowerCase()}
                                        href={`${v.location}`}
                                        onClick={(e) => { handleLinkClick(e, v) }}
                                        disabled={EmptyRoutes.reduce((a, c) => {
                                            if (a) return a;
                                            return c.test(v.location);
                                        }, false)}>
                                        {decodeURIComponent(getFriendlyName(v.title))}
                                    </Link>;
                                }
                            })}
                        </BCTrail>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
};

Breadcrumbs.propTypes = {
    path: PropTypes.string.isRequired,
    offset: PropTypes.number,
    header: PropTypes.bool,
};

export default Breadcrumbs;
